import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Form,
  ListGroup,
  InputGroup,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  Accordion,
  Dropdown,
} from "react-bootstrap";
import { NavBar } from "../navbar/navbar";
import { Lock } from "../icons";
import { useApi } from "../home/apiService";
import { useLanguage } from "../../i18n/LanguageProvider";
import { usePermissions } from "../../hooks/usePermissions";
import { useSearchParams } from "react-router-dom";
import { InsightsTab } from "./InsightsTab";
import { formatDateTime, formatRelative } from "./dates";
import { ForumTab } from "./ForumTab";
import { Analytics } from "../analytics/Analytics";
import { ArticlesTab } from "./ArticlesTab";
import "../common/PageHeader.css";
import "../../styles/sp-ui.css";
import "./Admin.css";

interface Permission {
  id: string;
  resource: string;
  action: string;
  description?: string;
}

interface Role {
  id: string;
  name: string;
  permissions: Permission[];
}

interface UserWithRoles {
  id: string;
  email: string;
  username?: string;
  image_link?: string;
  plan_id?: string;
  plan_name?: string;
  roles: { id: string; name: string }[];
  created_at?: string | null;
  last_login_at?: string | null;
  last_seen_at?: string | null;
}

type UserSort = "name" | "newest" | "active";

// Newest/most recent first; accounts without a timestamp sink to the bottom.
const byDateDesc = (a?: string | null, b?: string | null) =>
  (b ? Date.parse(b) : 0) - (a ? Date.parse(a) : 0);

interface Plan {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_hidden: boolean;
  max_active_days_per_month: number | null;
  api_requests_per_month: number | null;
  api_rpm: number | null;
  price_sek_per_month: number | null;
  roles: { id: string; name: string }[];
  userCount: number;
}

interface AppSetting {
  key: string;
  value: string;
  description: string | null;
  updated_at: string;
}

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  totalRequests: number;
  lastUsedAt: string | null;
  isActive: boolean;
  createdAt: string;
  ownerUsername: string | null;
  ownerEmail: string | null;
}

export const Admin = () => {
  const api = useApi();
  const { t, locale } = useLanguage();
  const { hasPermission } = usePermissions();
  const canViewAnalytics = hasPermission("analytics", "read");
  const canManageArticles = hasPermission("articles", "manage");
  const canSeeUserInsights = hasPermission("analytics", "user_insights");

  const canManageUsers = hasPermission("admin", "manage_users");
  const canManageRoles = hasPermission("admin", "manage_roles");
  const canManageSettings = hasPermission("admin", "manage_settings");
  const canDeleteUsers = hasPermission("admin", "delete_users");
  const canApproveUpgrades = hasPermission("plans", "approve_changes");
  const canModerateForum = hasPermission("forum", "moderate");

  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // User role editing
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);

  // Role editing
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState<
    string[]
  >([]);
  const [isNewRole, setIsNewRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const VALID_TABS = ["users", "settings", "apikeys", "telemetry", "accuracy", "upgrades", "forum"] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const initialTab =
    tabFromUrl && (VALID_TABS as readonly string[]).includes(tabFromUrl) ? tabFromUrl : "users";
  const [activeTab, setActiveTabState] = useState<string>(initialTab);
  const setActiveTab = (key: string) => {
    setActiveTabState(key);
    const next = new URLSearchParams(searchParams);
    if (key === "users") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const fromUrl = searchParams.get("tab") || "users";
    if (fromUrl !== activeTab && (VALID_TABS as readonly string[]).includes(fromUrl)) {
      setActiveTabState(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Telemetry state
  const [telemetry, setTelemetry] = useState<any | null>(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [telemetryDays, setTelemetryDays] = useState(30);

  const loadTelemetry = (days: number) => {
    setTelemetryLoading(true);
    api
      .fetchAdminTelemetry(days)
      .then((t) => setTelemetry(t))
      .catch(() => setTelemetry(null))
      .finally(() => setTelemetryLoading(false));
  };

  const changeTelemetryDays = (days: number) => {
    setTelemetryDays(days);
    loadTelemetry(days);
  };

  // Plan upgrade requests
  const [upgradeRequests, setUpgradeRequests] = useState<Array<{
    id: string;
    accountId: string;
    email: string;
    username: string | null;
    currentPlanName: string | null;
    requestedPlanName: string;
    note: string | null;
    requestedAt: string;
  }> | null>(null);
  const [upgradeRequestsLoading, setUpgradeRequestsLoading] = useState(false);

  // Load a tab's data when it opens, whether by click, a reload or a link with
  // ?tab=… (loading only on click left a reloaded Insights tab empty).
  useEffect(() => {
    if (activeTab === "telemetry" && !telemetry && !telemetryLoading) {
      loadTelemetry(telemetryDays);
    }
    if (activeTab === "upgrades" && upgradeRequests === null && !upgradeRequestsLoading) {
      setUpgradeRequestsLoading(true);
      api
        .fetchPlanChangeRequests("pending")
        .then((r) => setUpgradeRequests(r.requests))
        .catch(() => setUpgradeRequests([]))
        .finally(() => setUpgradeRequestsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [decidingId, setDecidingId] = useState<string | null>(null);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Plans state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [appSettings, setAppSettings] = useState<AppSetting[]>([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    description: "",
    roleIds: [] as string[],
    isHidden: false,
    maxActiveDaysPerMonth: "" as string,
    apiRequestsPerMonth: "" as string,
    apiRpm: "" as string,
    priceSekPerMonth: "" as string,
  });
  const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);
  const [assignPlanUserId, setAssignPlanUserId] = useState<string | null>(null);
  const [assignPlanId, setAssignPlanId] = useState<string>("");

  // Delete user state
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] =
    useState<UserWithRoles | null>(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showDeleteKeyModal, setShowDeleteKeyModal] = useState(false);
  const [deleteKeyTarget, setDeleteKeyTarget] = useState<ApiKey | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        usersResponse,
        rolesResponse,
        permissionsResponse,
        plansResponse,
        settingsResponse,
        apiKeysResponse,
      ] = await Promise.all([
        api.fetchAdminUsers(),
        api.fetchAdminRoles(),
        api.fetchAdminPermissions(),
        api.fetchAdminPlans().catch(() => ({ plans: [] })),
        api.fetchAdminSettings().catch(() => ({ settings: [] })),
        api.fetchApiKeys().catch(() => ({ apiKeys: [] })),
      ]);
      setUsers(usersResponse.users || []);
      setRoles(rolesResponse.roles || []);
      setPermissions(permissionsResponse.permissions || []);
      setPlans(plansResponse.plans || []);
      setAppSettings(settingsResponse.settings || []);
      setApiKeys(apiKeysResponse.apiKeys || []);
    } catch (err) {
      console.error("Error fetching admin data:", err);
      setError("Failed to load data. Make sure you have admin privileges.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [userSort, setUserSort] = useState<UserSort>("newest");

  const filteredUsers = users
    .filter((user) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        user.email.toLowerCase().includes(query) ||
        user.username?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) =>
      userSort === "newest"
        ? byDateDesc(a.created_at, b.created_at)
        : userSort === "active"
          ? byDateDesc(a.last_seen_at, b.last_seen_at)
          : a.email.localeCompare(b.email),
    );

  // User role editing handlers
  const handleDecideUpgrade = async (id: string, decision: "approve" | "reject") => {
    setDecidingId(id);
    try {
      if (decision === "approve") {
        await api.approvePlanChangeRequest(id);
      } else {
        await api.rejectPlanChangeRequest(id);
      }
      setUpgradeRequests((prev) => prev?.filter((r) => r.id !== id) || []);
      setToastMessage(
        decision === "approve" ? t("admin.upgradeApproved") : t("admin.upgradeRejected"),
      );
      setShowToast(true);
    } catch (e) {
      const msg = (e as Error)?.message || "Failed";
      setToastMessage(msg);
      setShowToast(true);
    } finally {
      setDecidingId(null);
    }
  };

  // Role editing handlers
  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setSelectedRolePermissions(role.permissions.map((p) => p.id));
    setIsNewRole(false);
    setNewRoleName("");
    setShowRoleModal(true);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setSelectedRolePermissions([]);
    setIsNewRole(true);
    setNewRoleName("");
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setSelectedRole(null);
    setSelectedRolePermissions([]);
    setIsNewRole(false);
    setNewRoleName("");
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedRolePermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSaveRole = async () => {
    setSaving(true);
    try {
      if (isNewRole) {
        if (!newRoleName.trim()) {
          alert("Role name is required");
          setSaving(false);
          return;
        }
        await api.createRole(newRoleName, selectedRolePermissions);
        await fetchData();
        handleCloseRoleModal();
        showSuccessToast(t("admin.success.roleCreated"));
      } else if (selectedRole) {
        await api.updateRolePermissions(
          selectedRole.id,
          selectedRolePermissions,
        );
        await fetchData();
        handleCloseRoleModal();
        showSuccessToast(t("admin.success.permissionsUpdated"));
      }
    } catch (err: any) {
      console.error("Error saving role:", err);
      alert(err?.message || "Failed to save role. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the role "${role.name}"? This will remove this role from all users.`,
      )
    ) {
      return;
    }

    try {
      await api.deleteRole(role.id);
      await fetchData();
      showSuccessToast(t("admin.success.roleDeleted"));
    } catch (err: any) {
      console.error("Error deleting role:", err);
      alert(err?.message || "Failed to delete role.");
    }
  };

  const displayUsername = (user: UserWithRoles) => {
    return user.username || user.email.split("@")[0];
  };

  const getRoleBadgeVariant = (roleName: string): string => {
    switch (roleName) {
      case "premium":
        return "warning";
      case "basic":
        return "info";
      case "free":
        return "secondary";
      default:
        return "primary";
    }
  };

  const getPermissionBadgeVariant = (action: string): string => {
    switch (action) {
      case "write":
        return "success";
      case "delete":
        return "danger";
      default:
        return "primary";
    }
  };

  const groupPermissionsByResource = (perms: Permission[]) => {
    const grouped: Record<string, Permission[]> = {};
    perms.forEach((p) => {
      if (!grouped[p.resource]) {
        grouped[p.resource] = [];
      }
      grouped[p.resource].push(p);
    });
    return grouped;
  };

  // Plan handlers
  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setIsNewPlan(true);
    setPlanForm({
      name: "",
      description: "",
      roleIds: [],
      isHidden: false,
      maxActiveDaysPerMonth: "",
      apiRequestsPerMonth: "",
      apiRpm: "",
      priceSekPerMonth: "",
    });
    setShowPlanModal(true);
  };

  const handleEditPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setIsNewPlan(false);
    setPlanForm({
      name: plan.name,
      description: plan.description || "",
      roleIds: plan.roles.map((r) => r.id),
      isHidden: !!plan.is_hidden,
      maxActiveDaysPerMonth: plan.max_active_days_per_month == null ? "" : String(plan.max_active_days_per_month),
      apiRequestsPerMonth: plan.api_requests_per_month == null ? "" : String(plan.api_requests_per_month),
      apiRpm: plan.api_rpm == null ? "" : String(plan.api_rpm),
      priceSekPerMonth: plan.price_sek_per_month == null ? "" : String(plan.price_sek_per_month),
    });
    setShowPlanModal(true);
  };

  const handleClosePlanModal = () => {
    setShowPlanModal(false);
    setPlanError(null);
    setSelectedPlan(null);
    setIsNewPlan(false);
    setPlanForm({
      name: "",
      description: "",
      roleIds: [],
      isHidden: false,
      maxActiveDaysPerMonth: "",
      apiRequestsPerMonth: "",
      apiRpm: "",
      priceSekPerMonth: "",
    });
  };

  const handlePlanRoleToggle = (roleId: string) => {
    setPlanForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const movePlan = async (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= plans.length || to >= plans.length) return;
    const reordered = [...plans];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setPlans(reordered.map((p, i) => ({ ...p, display_order: i })));
    try {
      await api.reorderPlans(reordered.map((p) => p.id));
    } catch (e) {
      console.error("Failed to reorder plans:", e);
      await fetchData();
    }
  };

  // Drag state — we track the dragged plan by id (stable across reorder previews)
  // and the visible slot under the cursor. While dragging, the rendered list is
  // a virtual "preview" splice so the other rows shift in real time. FLIP
  // animates each item from its prior bounding rect to the new one.
  const [dragPlanId, setDragPlanId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  const displayPlans = useMemo(() => {
    if (!dragPlanId || dropTargetIndex === null) return plans;
    const from = plans.findIndex((p) => p.id === dragPlanId);
    if (from === -1 || from === dropTargetIndex) return plans;
    const arr = [...plans];
    const [moved] = arr.splice(from, 1);
    arr.splice(dropTargetIndex, 0, moved);
    return arr;
  }, [plans, dragPlanId, dropTargetIndex]);


  const [planError, setPlanError] = useState<string | null>(null);

  const handleSavePlan = async () => {
    setPlanError(null);
    if (!planForm.name.trim()) {
      setPlanError(t("admin.planNameRequired"));
      return;
    }

    setSaving(true);
    try {
      const parseOptionalInt = (s: string): number | null => {
        const trimmed = s.trim();
        if (trimmed === "") return null;
        const n = parseInt(trimmed, 10);
        return Number.isFinite(n) ? n : null;
      };
      const data = {
        name: planForm.name,
        description: planForm.description || undefined,
        roleIds: planForm.roleIds,
        isHidden: planForm.isHidden,
        maxActiveDaysPerMonth: parseOptionalInt(planForm.maxActiveDaysPerMonth),
        apiRequestsPerMonth: parseOptionalInt(planForm.apiRequestsPerMonth),
        apiRpm: parseOptionalInt(planForm.apiRpm),
        priceSekPerMonth: parseOptionalInt(planForm.priceSekPerMonth),
      };

      if (isNewPlan) {
        await api.createPlan(data);
        showSuccessToast(t("admin.success.planCreated"));
      } else if (selectedPlan) {
        await api.updatePlan(selectedPlan.id, data);
        showSuccessToast(t("admin.success.planUpdated"));
      }
      await fetchData();
      handleClosePlanModal();
    } catch (err: any) {
      console.error("Error saving plan:", err);
      setPlanError(err?.message || t("admin.planSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (plan: Plan) => {
    if (!window.confirm(`${t("admin.confirmDeletePlan")} "${plan.name}"?`)) {
      return;
    }

    try {
      await api.deletePlan(plan.id);
      await fetchData();
      showSuccessToast(t("admin.success.planDeleted"));
    } catch (err: any) {
      console.error("Error deleting plan:", err);
      alert(err?.message || "Failed to delete plan.");
    }
  };

  const handleAssignPlan = (user: UserWithRoles) => {
    setAssignPlanUserId(user.id);
    setAssignPlanId(user.plan_id || "");
    setShowAssignPlanModal(true);
  };

  const handleCloseAssignPlanModal = () => {
    setShowAssignPlanModal(false);
    setAssignPlanUserId(null);
    setAssignPlanId("");
  };

  const handleSaveAssignPlan = async () => {
    if (!assignPlanUserId) return;

    setSaving(true);
    try {
      await api.assignUserPlan(assignPlanUserId, assignPlanId || null);
      await fetchData();
      handleCloseAssignPlanModal();
      showSuccessToast(t("admin.success.planAssigned"));
    } catch (err: any) {
      console.error("Error assigning plan:", err);
      alert(err?.message || "Failed to assign plan.");
    } finally {
      setSaving(false);
    }
  };

  // Delete user
  const handleDeleteUser = (user: UserWithRoles) => {
    setDeleteUserTarget(user);
    setShowDeleteUserModal(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!deleteUserTarget) return;

    setSaving(true);
    try {
      await api.deleteUser(deleteUserTarget.id);
      setShowDeleteUserModal(false);
      setDeleteUserTarget(null);
      await fetchData();
      showSuccessToast(t("admin.success.userDeleted"));
    } catch (err: any) {
      console.error("Error deleting user:", err);
      alert(err?.message || "Failed to delete user.");
    } finally {
      setSaving(false);
    }
  };

  // API Key handlers
  const handleToggleKey = async (apiKey: ApiKey) => {
    try {
      const response = await api.toggleApiKey(apiKey.id, !apiKey.isActive);
      setApiKeys((prev) =>
        prev.map((k) =>
          k.id === apiKey.id ? { ...k, isActive: response.apiKey.isActive } : k,
        ),
      );
      setToastMessage(response.message);
      setShowToast(true);
    } catch (err) {
      setError("Kunde inte uppdatera API-nyckel");
    }
  };

  const handleDeleteKey = (apiKey: ApiKey) => {
    setDeleteKeyTarget(apiKey);
    setShowDeleteKeyModal(true);
  };

  const handleConfirmDeleteKey = async () => {
    if (!deleteKeyTarget) return;
    setSaving(true);
    try {
      await api.deleteApiKey(deleteKeyTarget.id);
      setApiKeys((prev) => prev.filter((k) => k.id !== deleteKeyTarget.id));
      setShowDeleteKeyModal(false);
      setDeleteKeyTarget(null);
      setToastMessage("API-nyckel borttagen");
      setShowToast(true);
    } catch (err) {
      setError("Kunde inte ta bort API-nyckel");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDefaultPlan = async (planId: string) => {
    setSaving(true);
    try {
      await api.updateAdminSetting("default_signup_plan", planId);
      await fetchData();
      showSuccessToast(t("admin.success.settingUpdated"));
    } catch (err: any) {
      console.error("Error updating setting:", err);
      alert(err?.message || "Failed to update setting.");
    } finally {
      setSaving(false);
    }
  };

  const getDefaultSignupPlan = () => {
    return (
      appSettings.find((s) => s.key === "default_signup_plan")?.value || ""
    );
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  useEffect(() => {
    if (!showToast) return;
    const id = window.setTimeout(() => setShowToast(false), 3000);
    return () => window.clearTimeout(id);
  }, [showToast]);

  if (error) {
    return (
      <>
        <NavBar />
        <Container fluid className="page-container">
          <div className="page-header">
            <div className="page-header__content">
              <h1 className="page-header__title">{t("admin.title")}</h1>
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state-icon"><Lock size={40} style={{ opacity: 0.5 }} /></div>
            <p className="empty-state-text">{error}</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Container fluid className="sp-page">
        <div className="sp-page__head">
          <h1 className="sp-page__title">{t("admin.title")}</h1>
          <p className="sp-page__sub">{t("admin.subtitle")}</p>
        </div>

        {loading ? (
          <div className="sp-empty"><span className="sp-spinner" /></div>
        ) : (
          <div className="sp-layout">
            <aside className="sp-sidenav">
              {[
                { key: "users", label: t("admin.users"), show: true },
                { key: "settings", label: t("admin.settingsTab"), show: true },
                { key: "apikeys", label: "API-nycklar", show: true },
                { key: "telemetry", label: t("admin.insightsTab"), show: true },
                { key: "accuracy", label: t("admin.analyticsLink"), show: canViewAnalytics },
                { key: "upgrades", label: t("admin.upgradesTab"), show: canApproveUpgrades },
                { key: "forum", label: t("admin.forumTab"), show: canModerateForum },
                { key: "articles", label: t("articles.title"), show: canManageArticles },
              ]
                .filter((tab) => tab.show)
                .map((tab) => (
                  <button
                    key={tab.key}
                    className={`sp-sidenav__btn ${activeTab === tab.key ? "is-active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
            </aside>
            <div className="sp-admin-content">
            {/* Users Tab - User management with roles */}
            {activeTab === "users" && (<>
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-title">{t("admin.users")}</h3>
                  <span className="user-count">
                    {filteredUsers.length} {t("admin.usersCount")}
                  </span>
                </div>
                <div className="search-section">
                  <InputGroup className="search-input-group">
                    <InputGroup.Text className="search-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                      </svg>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t("admin.searchUsers")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </InputGroup>
                  <Form.Select
                    className="admin-user-sort"
                    value={userSort}
                    onChange={(e) => setUserSort(e.target.value as UserSort)}
                    aria-label={t("admin.sortUsers")}
                  >
                    <option value="newest">{t("admin.sortNewest")}</option>
                    <option value="active">{t("admin.sortActive")}</option>
                    <option value="name">{t("admin.sortName")}</option>
                  </Form.Select>
                </div>
                <ListGroup className="user-list admin-user-list">
                  {filteredUsers.map((user) => {
                    return (
                      <ListGroup.Item
                        key={user.id}
                        className="user-list-item admin-user-item"
                      >
                        <div className="user-list-item-content">
                          <div
                            className="sp-avatar"
                            aria-hidden="true"
                          >
                            {(displayUsername(user)[0] || user.email[0] || "?").toUpperCase()}
                          </div>
                          <div className="user-info flex-grow-1">
                            <div className="user-name">
                              {displayUsername(user)}
                            </div>
                            <div className="user-email">{user.email}</div>
                            <div className="user-meta">
                              {user.plan_name ? (
                                <span className="sp-chip">{user.plan_name}</span>
                              ) : (
                                <em>{t("admin.noPlan")}</em>
                              )}
                            </div>
                            <div className="admin-user-dates">
                              <span title={formatDateTime(user.created_at, locale)}>
                                {t("admin.userCreated")} {formatDateTime(user.created_at, locale)}
                              </span>
                              <span title={formatDateTime(user.last_login_at, locale)}>
                                {t("admin.userLastLogin")} {formatRelative(user.last_login_at, locale)}
                              </span>
                              <span title={formatDateTime(user.last_seen_at, locale)}>
                                {t("admin.userLastSeen")} {formatRelative(user.last_seen_at, locale)}
                              </span>
                            </div>
                          </div>
                          {canManageUsers && (
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                as="button"
                                className="sp-icon-btn"
                                aria-label={t("admin.assignPlan")}
                              >
                                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
                                </svg>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => handleAssignPlan(user)}
                                >
                                  {t("admin.changePlan")}
                                </Dropdown.Item>
                                {canDeleteUsers && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => handleDeleteUser(user)}
                                    >
                                      {t("admin.deleteUser")}
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              </div>
            </>)}

            {activeTab === "settings" && (<>
              {/* Plans Section */}
              <div className="section-card mb-4">
                <div className="section-header">
                  <h3 className="section-title">{t("admin.plans")}</h3>
                  {canManageSettings && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreatePlan}
                    >
                      + {t("admin.createPlan")}
                    </Button>
                  )}
                </div>

                {/* Default Signup Plan */}
                {canManageSettings && (
                  <div
                    className="mb-3 p-3"
                    style={{
                      background: "var(--bs-tertiary-bg)",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <Form.Group>
                      <Form.Label className="fw-bold">
                        {t("admin.defaultSignupPlan")}
                      </Form.Label>
                      <Form.Select
                        value={getDefaultSignupPlan()}
                        onChange={(e) =>
                          handleUpdateDefaultPlan(e.target.value)
                        }
                        disabled={saving}
                      >
                        <option value="">{t("admin.noDefaultPlan")}</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {t("admin.defaultPlanHint")}
                      </Form.Text>
                    </Form.Group>
                  </div>
                )}

                {plans.length === 0 ? (
                  <p className="text-muted">{t("admin.noPlans")}</p>
                ) : (
                  <Accordion
                    className="roles-accordion plans-reorder"
                    onDragOver={(e: React.DragEvent) => {
                      if (dragPlanId !== null) e.preventDefault();
                    }}
                  >
                    {/* STATIC slot numbers — keyed by position, never reorder. */}
                    {displayPlans.map((_p, i) => (
                      <div
                        key={`slot-${i}`}
                        className="plan-slot-number"
                        style={{ gridColumn: 1, gridRow: i + 1 }}
                      >
                        {i + 1}
                      </div>
                    ))}
                    {/* Plan rows — only these move. */}
                    {displayPlans.map((plan, index) => (
                      <Accordion.Item
                        key={plan.id}
                        eventKey={String(index)}
                        data-plan-id={plan.id}
                        style={{
                          gridColumn: 2,
                          gridRow: index + 1,
                          cursor: canManageSettings ? "grab" : undefined,
                        }}
                        draggable={canManageSettings}
                        onDragStart={(e: React.DragEvent) => {
                          setDragPlanId(plan.id);
                          setDropTargetIndex(index);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", plan.id);
                        }}
                        onDragOver={(e: React.DragEvent) => {
                          if (dragPlanId === null) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          if (dropTargetIndex !== index) setDropTargetIndex(index);
                        }}
                        onDrop={(e: React.DragEvent) => {
                          e.preventDefault();
                          if (dragPlanId) {
                            const from = plans.findIndex((p) => p.id === dragPlanId);
                            if (from !== -1 && dropTargetIndex !== null && from !== dropTargetIndex) {
                              movePlan(from, dropTargetIndex);
                            }
                          }
                          setDragPlanId(null);
                          setDropTargetIndex(null);
                        }}
                        onDragEnd={() => {
                          setDragPlanId(null);
                          setDropTargetIndex(null);
                        }}
                        className={
                          (dragPlanId === plan.id ? "is-dragging " : "") +
                          (plan.is_hidden ? "is-internal" : "")
                        }
                      >
                        <Accordion.Header>
                          <div className="role-accordion-header">
                            {canManageSettings && (
                              <span
                                className="plan-drag-handle me-2"
                                aria-hidden="true"
                                title={t("admin.planDragHint")}
                              >
                                <svg width="12" height="18" viewBox="0 0 12 18" fill="currentColor">
                                  <circle cx="3" cy="3" r="1.4" />
                                  <circle cx="9" cy="3" r="1.4" />
                                  <circle cx="3" cy="9" r="1.4" />
                                  <circle cx="9" cy="9" r="1.4" />
                                  <circle cx="3" cy="15" r="1.4" />
                                  <circle cx="9" cy="15" r="1.4" />
                                </svg>
                              </span>
                            )}
                            <Badge bg="dark" className="role-badge-large me-2">
                              {plan.name}
                            </Badge>
                            <span className="permission-count">
                              {plan.roles.length} {t("admin.planRolesCount")} ·{" "}
                              {plan.userCount} {t("admin.planUsers")}
                              {plan.price_sek_per_month != null &&
                                ` · ${plan.price_sek_per_month} kr/mån`}
                              {plan.max_active_days_per_month != null &&
                                ` · ${plan.max_active_days_per_month} ${t("admin.planDaysPerMonth")}`}
                            </span>
                            {plan.is_hidden && (
                              <Badge bg="secondary" className="ms-2">
                                {t("admin.planInternalBadge")}
                              </Badge>
                            )}
                            {getDefaultSignupPlan() === plan.id && (
                              <Badge bg="success" className="ms-2">
                                Default
                              </Badge>
                            )}
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          {plan.description && (
                            <p className="text-muted mb-2">
                              {plan.description}
                            </p>
                          )}
                          <div className="mb-2">
                            <strong>{t("admin.planRoles")}:</strong>{" "}
                            {plan.roles.length > 0 ? (
                              plan.roles.map((role) => (
                                <Badge
                                  key={role.id}
                                  bg={getRoleBadgeVariant(role.name)}
                                  className="me-1"
                                >
                                  {role.name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted">
                                {t("admin.noRoles")}
                              </span>
                            )}
                          </div>
                          <div className="plan-metrics mb-3">
                            <div className="plan-metric">
                              <span className="plan-metric__label">{t("admin.planPrice")}</span>
                              <span className="plan-metric__value">
                                {plan.price_sek_per_month != null
                                  ? `${plan.price_sek_per_month} kr/${t("admin.planPerMonth")}`
                                  : t("admin.planFree")}
                              </span>
                            </div>
                            <div className="plan-metric">
                              <span className="plan-metric__label">{t("admin.planActiveDays")}</span>
                              <span className="plan-metric__value">
                                {plan.max_active_days_per_month != null
                                  ? `${plan.max_active_days_per_month} ${t("admin.planDaysPerMonth")}`
                                  : t("admin.planUnlimited")}
                              </span>
                            </div>
                            <div className="plan-metric">
                              <span className="plan-metric__label">{t("admin.planApiRequests")}</span>
                              <span className="plan-metric__value">
                                {plan.api_requests_per_month != null
                                  ? `${plan.api_requests_per_month.toLocaleString()} /${t("admin.planPerMonth")}`
                                  : "—"}
                              </span>
                            </div>
                            <div className="plan-metric">
                              <span className="plan-metric__label">{t("admin.planApiRpm")}</span>
                              <span className="plan-metric__value">
                                {plan.api_rpm != null ? `${plan.api_rpm} rpm` : "—"}
                              </span>
                            </div>
                            <div className="plan-metric">
                              <span className="plan-metric__label">{t("admin.planUsers")}</span>
                              <span className="plan-metric__value">{plan.userCount}</span>
                            </div>
                          </div>
                          {canManageSettings && (
                            <div className="role-actions">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditPlan(plan)}
                                className="me-2"
                              >
                                {t("admin.editPlan")}
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeletePlan(plan)}
                              >
                                {t("admin.deletePlan")}
                              </Button>
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </div>

              {/* Roles and Permissions Row */}
              <Row className="settings-row">
                {/* Roles Section - Wider */}
                <Col lg={7} className="mb-4 mb-lg-0">
                  <div className="section-card h-100">
                    <div className="section-header">
                      <h3 className="section-title">
                        {t("admin.availableRoles")}
                      </h3>
                      {canManageRoles && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleCreateRole}
                        >
                          + {t("admin.createRole")}
                        </Button>
                      )}
                    </div>

                    <Accordion className="roles-accordion">
                      {roles.map((role, index) => (
                        <Accordion.Item key={role.id} eventKey={String(index)}>
                          <Accordion.Header>
                            <div className="role-accordion-header">
                              <Badge
                                bg={getRoleBadgeVariant(role.name)}
                                className="role-badge-large me-2"
                              >
                                {role.name}
                              </Badge>
                              <span className="permission-count">
                                {role.permissions.length}{" "}
                                {t("admin.permissionsCount")}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <div className="role-permissions-grid">
                              {Object.entries(
                                groupPermissionsByResource(role.permissions),
                              ).map(([resource, perms]) => (
                                <div key={resource} className="resource-group">
                                  <div className="resource-name">
                                    {resource}
                                  </div>
                                  <div className="resource-permissions">
                                    {perms.map((p) => (
                                      <span
                                        key={p.id}
                                        className="permission-badge-wrap"
                                      >
                                        <Badge
                                          bg={getPermissionBadgeVariant(
                                            p.action,
                                          )}
                                          className="permission-badge"
                                        >
                                          {p.action}
                                        </Badge>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              {role.permissions.length === 0 && (
                                <span className="no-permissions">
                                  {t("admin.noPermissions")}
                                </span>
                              )}
                            </div>
                            {canManageRoles && (
                              <div className="role-actions mt-3">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditRole(role)}
                                  className="me-2"
                                >
                                  {t("admin.editPermissions")}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteRole(role)}
                                >
                                  {t("admin.deleteRole")}
                                </Button>
                              </div>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                </Col>

                {/* Permissions Reference Section - Narrower */}
                <Col lg={5}>
                  <div className="section-card h-100">
                    <h3 className="section-title">
                      {t("admin.allPermissions")}
                    </h3>
                    <div className="permissions-compact">
                      {Object.entries(
                        groupPermissionsByResource(permissions),
                      ).map(([resource, perms]) => (
                        <div
                          key={resource}
                          className="permission-resource-compact"
                        >
                          <div className="resource-title-compact">
                            {resource}
                          </div>
                          <div className="permission-badges-compact">
                            {perms.map((p) => (
                              <span
                                key={p.id}
                                className="permission-badge-wrap"
                              >
                                <Badge
                                  bg={getPermissionBadgeVariant(p.action)}
                                  className="permission-badge-compact"
                                  title={
                                    p.description || `${p.resource}:${p.action}`
                                  }
                                >
                                  {p.action}
                                </Badge>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </>)}
            {activeTab === "apikeys" && (<>
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-title">
                    API-nycklar (för alla användare)
                  </h3>
                </div>
                {apiKeys.length === 0 ? (
                  <div className="empty-state">
                    <p>Inga API-nycklar skapade ännu.</p>
                  </div>
                ) : (
                  <ListGroup className="user-list admin-user-list">
                    {apiKeys.map((apiKey) => (
                      <ListGroup.Item
                        key={apiKey.id}
                        className="user-list-item admin-user-item"
                      >
                        <div className="user-list-item-content">
                          <div className="user-info flex-grow-1">
                            <div className="user-name">{apiKey.name}</div>
                            <div className="user-email">
                              <code>{apiKey.keyPrefix}...</code>
                            </div>
                            <div className="user-meta">
                              Skapad av:{" "}
                              {apiKey.ownerEmail ||
                                apiKey.ownerUsername ||
                                "system"}{" "}
                              &middot; {apiKey.totalRequests.toLocaleString()}{" "}
                              anrop
                              {apiKey.lastUsedAt && (
                                <>
                                  {" "}
                                  &middot; Senast använd{" "}
                                  {new Date(
                                    apiKey.lastUsedAt,
                                  ).toLocaleDateString()}
                                </>
                              )}
                              {!apiKey.lastUsedAt && (
                                <> &middot; Aldrig använd</>
                              )}{" "}
                              &middot; Skapad{" "}
                              {new Date(apiKey.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <Badge
                            bg={apiKey.isActive ? "success" : "secondary"}
                            className="me-2"
                          >
                            {apiKey.isActive ? "Aktiv" : "Inaktiv"}
                          </Badge>
                          {canManageSettings && (
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                as="button"
                                className="sp-icon-btn"
                                aria-label="Hantera"
                              >
                                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
                                </svg>
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                renderOnMount
                                popperConfig={{ strategy: "fixed" }}
                              >
                                <Dropdown.Item
                                  onClick={() => handleToggleKey(apiKey)}
                                >
                                  {apiKey.isActive ? "Inaktivera" : "Aktivera"}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => handleDeleteKey(apiKey)}
                                >
                                  Ta bort
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
            </>)}
            {activeTab === "telemetry" && (
              <InsightsTab
                telemetry={telemetry}
                telemetryLoading={telemetryLoading}
                canSeeUserInsights={canSeeUserInsights}
                days={telemetryDays}
                onDaysChange={changeTelemetryDays}
              />
            )}
            {activeTab === "accuracy" && canViewAnalytics && <Analytics embedded />}
            {activeTab === "forum" && canModerateForum && <ForumTab />}
            {activeTab === "articles" && canManageArticles && <ArticlesTab />}
            {activeTab === "upgrades" && canApproveUpgrades && (
              <div className="sp-card">
                <div className="sp-card__head">
                  <h3 className="sp-card__title">{t("admin.upgradesTitle")}</h3>
                </div>
                {upgradeRequestsLoading ? (
                  <div className="sp-empty"><span className="sp-spinner" /></div>
                ) : !upgradeRequests || upgradeRequests.length === 0 ? (
                  <div className="sp-empty">{t("admin.upgradesEmpty")}</div>
                ) : (
                  <table className="sp-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th>{t("admin.upgradesUser")}</th>
                        <th>{t("admin.upgradesCurrent")}</th>
                        <th>{t("admin.upgradesRequested")}</th>
                        <th>{t("admin.upgradesWhen")}</th>
                        <th style={{ textAlign: "right" }}>{t("admin.upgradesAction")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upgradeRequests.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{r.username || r.email.split("@")[0]}</div>
                            <div style={{ fontSize: "0.78rem", color: "var(--sp-text-dim)" }}>{r.email}</div>
                          </td>
                          <td><span className="sp-chip">{r.currentPlanName || "—"}</span></td>
                          <td><span className="sp-chip sp-chip--accent">{r.requestedPlanName}</span></td>
                          <td style={{ fontSize: "0.82rem", color: "var(--sp-text-dim)" }}>
                            {new Date(r.requestedAt).toLocaleString()}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button
                                className="sp-btn sp-btn--ghost sp-btn--sm"
                                onClick={() => handleDecideUpgrade(r.id, "reject")}
                                disabled={decidingId === r.id}
                              >
                                {t("admin.upgradesReject")}
                              </button>
                              <button
                                className="sp-btn sp-btn--primary sp-btn--sm"
                                onClick={() => handleDecideUpgrade(r.id, "approve")}
                                disabled={decidingId === r.id}
                              >
                                {t("admin.upgradesApprove")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {/* Edit/Create Role Modal */}
        <Modal
          show={showRoleModal}
          onHide={handleCloseRoleModal}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNewRole
                ? t("admin.createRole")
                : `${t("admin.editPermissionsFor")} "${selectedRole?.name}"`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isNewRole && (
              <Form.Group className="mb-4">
                <Form.Label>{t("admin.roleName")}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("admin.roleNamePlaceholder")}
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                />
              </Form.Group>
            )}

            <p className="modal-subtitle">{t("admin.selectPermissions")}</p>

            <div className="permissions-selector">
              {Object.entries(groupPermissionsByResource(permissions)).map(
                ([resource, perms]) => (
                  <div key={resource} className="permission-group-selector">
                    <h6 className="permission-group-title">{resource}</h6>
                    <div className="permission-checkboxes">
                      {perms.map((permission) => (
                        <Form.Check
                          key={permission.id}
                          type="checkbox"
                          id={`perm-${permission.id}`}
                          label={
                            <span className="permission-checkbox-label">
                              <Badge
                                bg={getPermissionBadgeVariant(
                                  permission.action,
                                )}
                                className="me-2"
                              >
                                {permission.action}
                              </Badge>
                              <span className="permission-checkbox-description">
                                {permission.description}
                              </span>
                            </span>
                          }
                          checked={selectedRolePermissions.includes(
                            permission.id,
                          )}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="permission-checkbox"
                        />
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseRoleModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveRole}
              disabled={saving}
            >
              {saving
                ? t("admin.saving")
                : isNewRole
                  ? t("admin.createRole")
                  : t("admin.saveChanges")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Create/Edit Plan Modal */}
        <Modal
          show={showPlanModal}
          onHide={handleClosePlanModal}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNewPlan ? t("admin.createPlan") : t("admin.editPlan")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {planError && (
              <div className="sp-alert sp-alert--danger mb-3">{planError}</div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planName")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("admin.planNamePlaceholder")}
                value={planForm.name}
                onChange={(e) =>
                  setPlanForm({ ...planForm, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planDescription")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder={t("admin.planDescriptionPlaceholder")}
                value={planForm.description}
                onChange={(e) =>
                  setPlanForm({ ...planForm, description: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planPriceSek")}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={planForm.priceSekPerMonth}
                onChange={(e) => setPlanForm({ ...planForm, priceSekPerMonth: e.target.value })}
                placeholder={t("admin.planNoPricePlaceholder")}
              />
              <Form.Text className="text-muted">{t("admin.planPriceSekHint")}</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planMaxActiveDays")}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={planForm.maxActiveDaysPerMonth}
                onChange={(e) => setPlanForm({ ...planForm, maxActiveDaysPerMonth: e.target.value })}
                placeholder={t("admin.planUnlimitedPlaceholder")}
              />
              <Form.Text className="text-muted">
                {t("admin.planMaxActiveDaysHint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planApiMonthly")}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={planForm.apiRequestsPerMonth}
                onChange={(e) => setPlanForm({ ...planForm, apiRequestsPerMonth: e.target.value })}
                placeholder={t("admin.planNoAccessPlaceholder")}
              />
              <Form.Text className="text-muted">
                {t("admin.planApiMonthlyHint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planApiRpm")}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={planForm.apiRpm}
                onChange={(e) => setPlanForm({ ...planForm, apiRpm: e.target.value })}
                placeholder={t("admin.planNoRpmPlaceholder")}
              />
              <Form.Text className="text-muted">
                {t("admin.planApiRpmHint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="plan-is-hidden"
                label={t("admin.planIsInternal")}
                checked={planForm.isHidden}
                onChange={(e) => setPlanForm({ ...planForm, isHidden: e.target.checked })}
              />
              <Form.Text className="text-muted">
                {t("admin.planIsInternalHint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("admin.planRoles")}</Form.Label>
              <div className="role-checkboxes">
                {roles.map((role) => (
                  <Form.Check
                    key={role.id}
                    type="checkbox"
                    id={`plan-role-${role.id}`}
                    label={
                      <span className="role-checkbox-label">
                        <Badge
                          bg={getRoleBadgeVariant(role.name)}
                          className="me-2"
                        >
                          {role.name}
                        </Badge>
                        <span className="role-checkbox-description">
                          {role.permissions.length}{" "}
                          {t("admin.permissionsCount")}
                        </span>
                      </span>
                    }
                    checked={planForm.roleIds.includes(role.id)}
                    onChange={() => handlePlanRoleToggle(role.id)}
                    className="role-checkbox"
                  />
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClosePlanModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSavePlan}
              disabled={saving}
            >
              {saving
                ? t("admin.saving")
                : isNewPlan
                  ? t("admin.createPlan")
                  : t("admin.saveChanges")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Assign Plan Modal */}
        <Modal
          show={showAssignPlanModal}
          onHide={handleCloseAssignPlanModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("admin.assignPlan")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t("admin.userPlan")}</Form.Label>
              <Form.Select
                value={assignPlanId}
                onChange={(e) => setAssignPlanId(e.target.value)}
              >
                <option value="">{t("admin.noPlan")}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({plan.roles.length} {t("admin.planRolesCount")}
                    )
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {t("admin.defaultPlanHint")}
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseAssignPlanModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveAssignPlan}
              disabled={saving}
            >
              {saving ? t("admin.saving") : t("admin.assignPlan")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete User Confirmation Modal */}
        <Modal
          show={showDeleteUserModal}
          onHide={() => {
            setShowDeleteUserModal(false);
            setDeleteUserTarget(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("admin.deleteUser")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t("admin.confirmDeleteUser")}</p>
            {deleteUserTarget && (
              <p className="fw-bold">
                {displayUsername(deleteUserTarget)} ({deleteUserTarget.email})
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteUserModal(false);
                setDeleteUserTarget(null);
              }}
              disabled={saving}
            >
              {t("admin.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDeleteUser}
              disabled={saving}
            >
              {saving ? t("admin.saving") : t("admin.deleteUser")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete API Key Confirmation Modal */}
        <Modal
          show={showDeleteKeyModal}
          onHide={() => setShowDeleteKeyModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Ta bort API-nyckel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Är du säker på att du vill ta bort API-nyckeln{" "}
              <strong>{deleteKeyTarget?.name}</strong> permanent?
            </p>
            <p className="text-muted">
              Alla applikationer som använder denna nyckel förlorar åtkomst
              omedelbart.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteKeyModal(false)}
            >
              Avbryt
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDeleteKey}
              disabled={saving}
            >
              {saving ? "Tar bort..." : "Ta bort"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Success Toast — sp-ui themed */}
        {showToast && (
          <div className="sp-toast sp-toast--success" role="status" aria-live="polite">
            <span className="sp-toast__icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="sp-toast__body">{toastMessage}</span>
            <button
              type="button"
              className="sp-toast__close"
              onClick={() => setShowToast(false)}
              aria-label="Close"
            >×</button>
          </div>
        )}
      </Container>
    </>
  );
};
